import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface HeaderProps {
  onMenuToggle: () => void;
  title?: string;
}

const notifications = [
  { id: 1, icon: 'ri-file-list-3-line', color: 'text-[#3b82f6]', bg: 'bg-blue-50', text: 'Yeni başvuru: Zeynep Kaya — Matematik', time: '5 dk önce' },
  { id: 2, icon: 'ri-money-dollar-circle-line', color: 'text-[#f59e0b]', bg: 'bg-amber-50', text: 'Bekleyen dekont onayı: Can Yıldız', time: '20 dk önce' },
  { id: 3, icon: 'ri-calendar-check-line', color: 'text-[#10b981]', bg: 'bg-emerald-50', text: 'Deneme dersi: Ahmet Demir / Emre K. — 16:00', time: '1 saat önce' },
  { id: 4, icon: 'ri-user-star-line', color: 'text-[#7c6af5]', bg: 'bg-purple-50', text: 'Hak ediş hesabı güncellendi: Selin Yılmaz', time: '3 saat önce' },
];

export default function Header({ onMenuToggle, title }: HeaderProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { profile, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const displayName = profile?.ad_soyad ?? 'Yönetici';
  const displayEmail = profile?.email ?? '';

  return (
    <header className="h-16 bg-white border-b border-gray-100 flex items-center px-4 md:px-6 gap-4 flex-shrink-0 relative z-20">
      {/* Hamburger */}
      <button
        onClick={onMenuToggle}
        className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600 transition-colors cursor-pointer lg:hidden"
      >
        <i className="ri-menu-line text-xl" />
      </button>

      {/* Page title */}
      {title && (
        <div className="hidden md:block">
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 w-56">
        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
          <i className="ri-search-line text-sm" />
        </div>
        <input
          type="text"
          placeholder="Öğrenci, öğretmen ara..."
          className="flex-1 bg-transparent text-sm text-gray-600 placeholder-gray-400 outline-none"
        />
      </div>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => { setShowNotifications(!showNotifications); setShowProfile(false); }}
          className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-notification-3-line text-xl" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
        </button>

        {showNotifications && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="font-semibold text-sm text-gray-800">Bildirimler</p>
              <span className="text-xs bg-red-100 text-red-600 font-semibold px-2 py-0.5 rounded-full">{notifications.length} Yeni</span>
            </div>
            <ul className="max-h-72 overflow-y-auto">
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50 cursor-pointer"
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${n.bg} flex-shrink-0 mt-0.5`}>
                    <i className={`${n.icon} text-sm ${n.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 leading-relaxed">{n.text}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{n.time}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="px-4 py-2.5 text-center">
              <button className="text-xs text-[#3b5bdb] font-semibold hover:underline cursor-pointer whitespace-nowrap">
                Tüm bildirimleri gör
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profile */}
      <div className="relative">
        <button
          onClick={() => { setShowProfile(!showProfile); setShowNotifications(false); }}
          className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1a2f5a] flex-shrink-0">
            <span className="text-white text-xs font-bold">{displayName.charAt(0)}</span>
          </div>
          <div className="hidden md:block text-left">
            <p className="text-xs font-semibold text-gray-800 leading-tight">{displayName}</p>
            <p className="text-xs text-gray-400 leading-tight">Yönetici</p>
          </div>
          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-arrow-down-s-line text-sm" />
          </div>
        </button>

        {showProfile && (
          <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800">{displayName}</p>
              <p className="text-xs text-gray-400 truncate">{displayEmail}</p>
            </div>
            <ul className="py-1">
              <li className="border-t border-gray-100 mt-1">
                <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 w-full text-left cursor-pointer whitespace-nowrap">
                  <i className="ri-logout-box-line" />
                  Çıkış Yap
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </header>
  );
}